import { BlogPost, BLOG_AUTHOR } from '../../../types/blogTypes';

const post: BlogPost = {
  slug: 'mock-interview-in-your-pocket',
  title: 'Practice an Interview With AI Before the Real One',
  description:
    'Practice an interview with AI: build 4 or 5 reusable stories, rehearse the classic questions out loud with Sage, and walk in with answers you have said before.',
  category: 'Career',
  icon: 'user-tie',
  publishedAt: '2026-09-30',
  author: BLOG_AUTHOR,
  product: 'chot-buddies',
  buddy: 'Sage',
  keywords: ['practice interview with ai', 'mock interview', 'interview preparation', 'career coach app'],
  body: `The interview is tomorrow at 10. You have read the job post four times, ironed the shirt, and silently rehearsed your answer to "tell me about yourself" in the shower. It was brilliant. Then you imagine saying it to an actual human, and the first sentence comes out as "So, um, I guess I'm a person who..."

Interviews are won by answers you have said out loud, and most people rehearse in their heads, where everything sounds fluent. The mouth is a different muscle. Here is how to practice an interview with AI, so your answers leave your mouth for the first time before the real thing, not during it.

## Start with the company and the role

Before you rehearse anything, know what you are rehearsing for. Spend 30 focused minutes on three things.

- **The role.** Read the job post and underline the three or four things they ask for most. Those are the themes your answers should touch.
- **The company.** What do they make, who do they serve, what have they announced lately? Read their own site and recent news, not guesses.
- **The people.** If you know who is interviewing you, look at their role. It tells you what they will care about.

Then write one sentence: "I'm a good fit for this because..." Everything else you prepare should support that sentence.

## Build 4 or 5 stories you can reuse

Most interview questions are the same few questions in different clothes. Prepare a handful of true stories from your work, school or life, and bend them to fit.

Each story needs three parts, in this order:

1. **The situation.** What was going on? Two sentences, no backstory.
2. **What you did.** This is the heart of it. Say "I," not "we," and be specific about your own actions and choices.
3. **The result.** What changed because of it? Numbers help if you have them, but a clear outcome and what you learned will do.

Pick stories that cover different ground: something you led, something that went wrong, a disagreement, a time you learned fast, and one you are proud of. Write each as five or six bullet points, not a script. Remember the shape, not the paragraph.

## Know the classic questions

Expect some version of each of these. Here is how to approach them.

- **Tell me about yourself.** This is not your life story. Give a 60-second path: where you are now, a thread from your past that led here, and why this role is next.
- **Strengths.** Name one or two that match the job post, and attach a story as proof.
- **Weaknesses.** Pick a real one that is not fatal to the role, then say what you are doing about it. "I work too hard" convinces nobody.
- **Why this role, why this company?** Use your research. Be specific about what draws you, not just that it looks like a good opportunity.
- **A failure.** Own it plainly, say what you changed, and stop. Do not turn it into a disguised brag.
- **A conflict.** Describe the disagreement fairly, focus on how you listened and handled it, and never make the other person the villain.
- **Where do you see yourself in five years?** They want ambition that fits the role, not a vow. Talk about what you want to learn and take on.

## Prepare your questions for them

At the end you will be asked, "Do you have any questions for us?" Saying "No, I think you covered it" is a small missed chance. Bring three, written down.

- What does success look like in this role after the first six months?
- What is the biggest challenge the team is working through right now?
- How does the team handle feedback and decisions?

Good questions show you are thinking about doing the job, not just getting it. If salary comes up later, [Win the Salary Negotiation Before You Walk In](/blog/negotiate-your-salary-before-you-walk-in/) covers how to prepare for that conversation too.

## Handle the nerves and the blank

Everyone gets nervous. The goal is not to remove it but to keep it from steering.

A blank moment is survivable. Say, "Good question, let me think for a second," and then take that second. Silence feels longer to you than to them. If you still lose the thread, it is fine to say, "Can I come back to that in a moment?" or to ask them to repeat the question.

Slow your pace on purpose. Nervous people speed up, and a calm, slower voice reads as confidence. Keep water nearby, breathe out longer than you breathe in before you start, and remember that the interviewer wants you to do well. They have a seat to fill. If you are still deciding what to put on, [Dressing for the Interview, the First Date and the Wedding](/blog/dressing-for-interview-date-wedding/) can take that decision off your mind the night before.

## Practice an interview with AI, out loud, with Sage

[Chot Buddies](/products/chot-buddies/) gives you Sage, the Career Coach buddy, built for work, interviews and next steps. Chot Buddies has voice mode, a real-time voice conversation, so you can answer Sage out loud on a walk or at your desk, as many times as you like. No one is waiting on the other end, so you can ramble, restart and try again.

Paste in these three prompts, one per round.

First, set up a realistic mock interview:

> Act as a tough but fair interviewer for a [job title] role at [company type]. Ask me one question at a time, wait for my answer, and ask a follow-up the way a real interviewer would. Start with "tell me about yourself."

Second, stress-test one of your stories:

> Here is a story I plan to use for "tell me about a conflict": [paste your bullet points]. Ask me three follow-up questions an interviewer might ask, one at a time, and push back if my answer is vague.

Third, get feedback after a round:

> Based on my answers so far, give me honest feedback: what was clear, what was rambling, and what I should cut. Then suggest a tighter way to say my weakest answer.

Answer aloud, listen to the follow-ups, and mark every spot where you stumble. Rewrite those first. If you want to get comfortable hearing blunt notes on your work, [Everyone Says It's a Great Idea. Scarlett Doesn't.](/blog/honest-feedback-when-everyone-says-yes/) is about asking for that kind of honesty.

One caution. Sage is a practice partner, not a guarantee. Real interviewers are unpredictable, and no rehearsal can promise an offer. What it can do is make sure the words are not coming out for the first time when it counts.

## The follow-up note

Within 24 hours, send a short thank-you message of three or four sentences: thank them for their time, name one specific thing you enjoyed discussing, and say in a line why you are excited about the role. If you forgot to mention something useful, add it in one sentence. Short, warm and prompt beats long and clever.

## Before you walk in

Preparation removes surprises. Know the role, have your stories ready, and say each answer aloud at least three times. Tonight, open Chot Buddies (it is free to start), pick Sage, paste the first prompt above, and answer five questions out loud. Tomorrow morning, run one more round on the way in. You will walk into the room having already done it twice.
`,
};

export default post;
